import { Component, type ErrorInfo, type ReactNode } from 'react'
import { t } from '../i18n'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  where: string
}

/** Граница ошибок кабинета.
 *
 *  Без неё любое исключение — в рендере, в эффекте, в обработчике карты — уносит
 *  ВСЁ дерево: React размонтирует корень, и в окне остаётся пустой фон. Именно так
 *  выглядел сбой при длинной протяжке ползунка окна: не «ошибка», а чистый экран,
 *  по которому нечего диагностировать.
 *
 *  Здесь мы не чиним причину, а делаем сбой читаемым: текст ошибки на виду, его
 *  можно скопировать и прислать, и есть чем вернуться к работе. Каркас с рельсом
 *  при этом остаётся живым — падает только содержимое раздела. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, where: '' }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Стек компонентов в консоль: он говорит, ЧТО упало, точнее самого сообщения.
    // Сообщение в консоль — для разработчика, а не для пользователя: оно не
    // переводится и остаётся английским в обоих языках.
    console.error('[cabinet] unhandled error', error, info.componentStack)
    this.setState({ where: info.componentStack ?? '' })
  }

  private reset = () => this.setState({ error: null, where: '' })

  private copy = () => {
    const { error, where } = this.state
    const text = [
      `${error?.name}: ${error?.message}`,
      error?.stack ?? '',
      where,
      location.href,
    ].join('\n')
    navigator.clipboard?.writeText(text)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-xl rounded-xl border border-hull bg-deep p-5 text-sm">
          <div className="mb-1 font-semibold text-foam">{t('error.sectionCrashed')}</div>
          <p className="mb-3 text-mist">
            {t('error.sectionCrashedHint')}
          </p>
          <pre className="mb-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-trench p-3 text-xs text-mist">
            {error.name}: {error.message}
          </pre>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-lg bg-sonar-dim px-3 py-1.5 font-medium text-abyss"
            >
              {t('error.retry')}
            </button>
            <button
              type="button"
              onClick={() => location.reload()}
              className="rounded-lg border border-hull px-3 py-1.5 text-mist hover:text-foam"
            >
              {t('error.reload')}
            </button>
            <button
              type="button"
              onClick={this.copy}
              className="rounded-lg px-3 py-1.5 text-foam hover:bg-haze"
            >
              {t('common.copy')}
            </button>
          </div>
        </div>
      </div>
    )
  }
}
